import { useEffect, useState } from 'react';
import { Badge, Button, Group, Text, Stack } from '@mantine/core';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import { DateChip } from '@ayocore/platform/client/components/elements';
import { useTaskRepository } from '@ayocore/tasks/client/rpc';
import { UserCell } from '@ayocore/platform/identity/client/cells';
import { EditTaskForm } from '@ayocore/tasks/client/components/EditTaskForm';
import { CompleteTaskModal } from '@ayocore/erp/crm/client/components/tasks/components/CompleteTaskModal';
import { CrmCreateTaskForm } from '@ayocore/erp/crm/client/components/tasks/CrmCreateTaskForm';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import type { Task } from '@ayocore/tasks/schemas/task.schema';
import type { User } from '@ayocore/platform/identity/schemas/user.schema';
import logger from '@ayocore/platform/common/helpers/logger';

interface PartnerTasksProps {
  partnerUuid: string;
}

function getAssigneeUser(
  assignee: unknown
): { id: number; name?: string | null; email?: string | null } | null {
  if (!assignee || typeof assignee !== 'object') return null;
  const u = assignee as User;
  if (!u.id) return null;
  return { id: u.id, name: u.name ?? null, email: u.email ?? null };
}

function getPriorityColor(dueDate: string | null | undefined): string {
  if (!dueDate) return 'blue';
  const date = new Date(dueDate);
  const now = new Date();
  if (date < now) return 'red';
  if (date <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) return 'orange';
  return 'green';
}

const ENTITY_TYPE = 'crm::PartnerRepository';

/**
 * Блок «Задачи» — отображает задачи партнёра с инлайн-созданием.
 * Позволяет добавить задачу по названию, указать дату и ответственного прямо в списке.
 */
export function PartnerTasks({ partnerUuid }: PartnerTasksProps) {
  const taskRepository = useTaskRepository();
  const editTaskModal = useModal(EditTaskForm);
  const completeModal = useModal(CompleteTaskModal);
  const createTaskModal = useModal(CrmCreateTaskForm);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey(k => k + 1);

  // Загружаем задачи.
  // Лоадер показываем только при первом рендере — при последующих refresh() данные
  // обновляются в фоне без мигания (setInitialLoading не вызывается повторно).
  useEffect(() => {
    if (!partnerUuid) return;
    let disposed = false;
    (async () => {
      try {
        const data = await taskRepository
          .where('entity_uuid', partnerUuid)
          .where('entity_type', ENTITY_TYPE)
          .filterRelevant()
          .orderBy('created_at', 'desc');
        if (!disposed) setTasks(Array.isArray(data) ? data : []);
      } catch {
        if (!disposed) setTasks([]);
      } finally {
        if (!disposed) setInitialLoading(false);
      }
    })();
    return () => {
      disposed = true;
    };
  }, [partnerUuid, taskRepository, refreshKey]);

  const openCreateTask = () => {
    createTaskModal.onClose(() => refresh());
    createTaskModal.open(
      {
        entityUuid: partnerUuid,
        entityType: ENTITY_TYPE,
        onCreated: () => {
          createTaskModal.close();
          refresh();
        },
        title: 'Новая задача',
      },
      { size: 'lg', inputSize: 'sm' }
    );
  };

  const handleUpdateDueDate = async (taskId: number, date: Date | null) => {
    try {
      await taskRepository.update(taskId, { due_date: date ? date.toISOString() : null });
      refresh();
    } catch (e) {
      logger.error('[PartnerTasks] Failed to update due_date', e);
    }
  };

  const openComplete = (task: Task) => {
    completeModal.onClose(() => refresh());
    completeModal.open(
      {
        task,
        onCompleted: () => {
          completeModal.close();
          refresh();
        },
        onCancel: () => completeModal.close(),
        title: `Завершить задачу «#${task.id} ${task.title}»`,
      },
      { size: 'md' }
    );
  };

  if (initialLoading) {
    return (
      <ViewPanel>
        <Text fw={400} style={{ fontSize: 14 }}>
          Задачи
        </Text>
        <Text size='sm' c='dimmed'>
          Загрузка...
        </Text>
      </ViewPanel>
    );
  }

  return (
    <ViewPanel>
      <style>{`
        .task-item:hover .date-hint { opacity: 1 !important; }
        .task-item:not(:last-child) {
          border-bottom: 1px solid var(--mantine-color-gray-2);
          padding-bottom: 6px;
        }
      `}</style>

      <Text fw={400} style={{ fontSize: 14, marginBottom: 8 }}>
        Задачи
      </Text>

      {tasks.length > 0 && (
        <Stack gap='xs' mb='xs'>
          {tasks.map(task => (
            <div
              key={task.id}
              className='task-item'
              style={{
                // Grid с фикс px-колонками: на любой ширине дата/ответственный/
                // действие остаются компактными, а title растягивается на остаток.
                display: 'grid',
                gridTemplateColumns: '1fr 110px 200px 120px',
                columnGap: 8,
                alignItems: 'center',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={() => {
                editTaskModal.onClose(() => refresh());
                editTaskModal.open(
                  {
                    taskId: task.id,
                    onTaskUpdated: () => {
                      editTaskModal.close();
                      refresh();
                    },
                    title: 'Редактировать задачу',
                  },
                  { size: 'lg', inputSize: 'sm' }
                );
              }}
            >
              <Text
                size='sm'
                c='blue'
                style={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Text component='span' inherit ff='monospace' mr={6}>
                  #{task.id}
                </Text>
                {task.title}
              </Text>

              <div onClick={e => e.stopPropagation()} style={{ minWidth: 0 }}>
                <DateChip
                  value={task.due_date as string | null}
                  label='Срок'
                  hintClassName='date-hint'
                  color={getPriorityColor(task.due_date as string | null)}
                  onSave={date => void handleUpdateDueDate(task.id, date)}
                  size='sm'
                />
              </div>

              <div onClick={e => e.stopPropagation()} style={{ minWidth: 0 }}>
                <UserCell user={getAssigneeUser(task.assignee)} fallback='Не назначен' />
              </div>

              <div onClick={e => e.stopPropagation()} style={{ justifySelf: 'end' }}>
                {task.task_status?.is_final ? (
                  <Badge
                    color='green'
                    variant='light'
                    size='sm'
                    tt='none'
                    leftSection={<IconCheck size={10} />}
                  >
                    Выполнена
                  </Badge>
                ) : (
                  <Button
                    size='compact-xs'
                    leftSection={<IconCheck size={12} />}
                    onClick={() => openComplete(task)}
                  >
                    Завершить
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Stack>
      )}

      <Group justify='flex-start' mt={tasks.length > 0 ? 12 : 0}>
        <Button
          size='compact-xs'
          variant='light'
          leftSection={<IconPlus size={12} />}
          onClick={openCreateTask}
        >
          Новая задача
        </Button>
      </Group>
    </ViewPanel>
  );
}
